'use client';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AccordionContextValue } from './accordion.types';

export interface AccordionProviderProps {
  defaultExpanded?: string[];
  children: React.ReactNode;
  selectionMode?: 'single' | 'multiple';
}

const AccordionContext = createContext<AccordionContextValue | null>(null);

export function AccordionProvider({
  defaultExpanded = [],
  selectionMode = 'single',
  children,
}: AccordionProviderProps) {
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set(defaultExpanded));

  const contentRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    setOpenIds(new Set(defaultExpanded));
  }, [defaultExpanded]);

  const toggle = useCallback(
    (id: string) => {
      setOpenIds((prev) => {
        const next = new Set(prev);
        const isAlreadyOpen = next.has(id);

        if (selectionMode === 'single') {
          next.clear();
          if (!isAlreadyOpen) {
            next.add(id);
          }
          return next;
        }

        if (isAlreadyOpen) {
          next.delete(id);
        } else {
          next.add(id);
        }

        return next;
      });
    },
    [selectionMode],
  );

  const isOpen = useCallback((id: string) => openIds.has(id), [openIds]);

  const getHeight = useCallback((id: string) => contentRefs.current[id]?.offsetHeight || 0, []);

  const value = useMemo<AccordionContextValue>(
    () => ({
      openIds,
      setOpenIds,
      toggle,
      isOpen,
      contentRefs,
      getHeight,
      selectionMode,
    }),
    [openIds, toggle, isOpen, getHeight, selectionMode],
  );

  return React.createElement(AccordionContext.Provider, { value }, children);
}

export function useAccordion(): AccordionContextValue {
  const ctx = useContext(AccordionContext);
  if (!ctx) {
    throw new Error('useAccordion must be used within an AccordionProvider');
  }
  return ctx;
}
